import React, { useState, useEffect, useCallback } from 'react';
import cx from 'classnames';
import {
  useSearchUnsplashQuery,
  SearchUnsplashQuery,
} from '@trello/graphql/generated';
import { debounce } from 'underscore';
import { Spinner } from 'app/src/components/Spinner';
import { forTemplate } from 'app/common/lib/util/i18n';
import { InfiniteList, IterableType } from 'app/src/components/InfiniteList';
import { SearchResultImageButton } from './SearchResultImageButton';
import styles from './SearchResults.less';

const format = forTemplate('card_cover_chooser');

const PER_PAGE = 12;

type UnsplashPhotos = SearchUnsplashQuery['unsplashPhotos'];

interface SearchResultsProps {
  cardId: string;
  query: string;
  onSetCover: () => void;
}

export const SearchResults: React.FunctionComponent<SearchResultsProps> = ({
  cardId,
  query,
  onSetCover,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [searchResults, setSearchResults] = useState<UnsplashPhotos>([]);

  const { data, error, loading } = useSearchUnsplashQuery({
    skip: !searchQuery.trim().length,
    variables: {
      query: searchQuery,
      perPage: PER_PAGE,
      page: page,
    },
  });

  const debouncedOnSearchChange = useCallback(
    debounce((value: string) => {
      setPage(1);
      setSearchQuery(value);
      setIsTyping(false);
    }, 500),
    [setPage, setSearchQuery, setIsTyping],
  );

  useEffect(
    () => {
      setIsTyping(true);
      debouncedOnSearchChange(query);
    },
    [query, debouncedOnSearchChange, setIsTyping],
  );

  const photos = data && data.unsplashPhotos ? data.unsplashPhotos : [];

  useEffect(
    () => {
      if (page === 1) {
        setSearchResults(photos);
      } else {
        setSearchResults((prevSearchResults) => {
          const existing = new Set(prevSearchResults.map((entry) => entry.id));
          return [
            ...prevSearchResults,
            ...photos.filter((photo) => !existing.has(photo.id)),
          ];
        });
      }
    },
    [photos, page],
  );

  const hasMorePhotos = photos.length === PER_PAGE;

  if (error) {
    return <div>{format('something-went-wrong')}</div>;
  }

  if (!loading && searchQuery.trim().length && !searchResults.length) {
    return <div className="quiet">{format('no-search-results')}</div>;
  }

  const showSpinner = isTyping || (loading && !searchResults.length);
  const showResults = !showSpinner;

  return (
    <>
      {showSpinner && <Spinner centered small />}
      <InfiniteList
        axis="y"
        threshold="30%"
        spinnerClassName={styles.hideInfiniteListSpinner}
        itemCount={searchResults.length}
        pageSize={PER_PAGE}
        awaitMore={!loading && hasMorePhotos}
        loadMore={() => setPage(page + 1)}
        isLoading={loading}
        itemsRenderer={(
          items: IterableType,
          ref: (instance: React.ReactInstance) => void,
        ) => (
          <div
            className={cx(styles.coverTiles, {
              [styles.modFixedHeight]: showResults,
            })}
            ref={(div: HTMLDivElement) => ref(div)}
          >
            {items}
          </div>
        )}
        renderItem={(index: number, key: number) => {
          const { urls, links, user } = searchResults[index];
          return (
            <SearchResultImageButton
              key={key}
              urls={urls}
              links={links}
              user={user}
              index={index}
              cardId={cardId}
              onSetCover={onSetCover}
            />
          );
        }}
      />
      <div className={styles.searchResultsFooter}>
        {format('photos-from-unsplash', {
          unsplashLink: (
            <a
              key="a"
              className={styles.unsplashLink}
              href="https://unsplash.com?utm_source=trello&utm_medium=referral&utm_campaign=api-credit"
              rel="noreferrer noopener"
              target="_blank"
            >
              Unsplash
            </a>
          ),
        })}
      </div>
    </>
  );
};
